import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Badge,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  CalendarMonth as CalendarIcon,
  ViewWeek as ViewWeekIcon,
  ViewDay as ViewDayIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, where, orderBy, deleteDoc, doc, updateDoc, addDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { Shift, shiftTypeLabels, shiftStatusLabels, shiftStatusColors } from '../types/shift';
import { ShiftRequest } from '../types/shift-request';
import { Employee } from '../types/employee';
import DailyView from '../components/ShiftGantt/DailyView';
import WeeklyView from '../components/ShiftGantt/WeeklyView';

import { getBusinessDate } from '../utils/businessDate';
import { clamp } from '../utils/numberGuard';
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Shifts() {
  const { shop } = useAuth();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftRequests, setShiftRequests] = useState<ShiftRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // タブ状態
  const [tabValue, setTabValue] = useState(0);

  // ガントチャート用の選択日
  const [selectedDate, setSelectedDate] = useState(new Date());

  // ダイアログ状態
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Shift | null>(null);

  // フォームデータ
  const [formData, setFormData] = useState({
    employeeId: '',
    shiftDate: '',
    startTime: '09:00',
    endTime: '18:00',
    breakMinutes: '60',
    shiftType: 'regular' as Shift['shiftType'],
    note: '',
    status: 'pending' as Shift['status'],
  });

  // 募集フォームデータ
  const [requestFormData, setRequestFormData] = useState({
    shiftDate: '',
    startTime: '18:00',
    endTime: '23:00',
    breakMinutes: '0',
    requiredStaffCount: '1',
    position: '',
    message: '',
  });

  // シフト変更希望
  const [changeRequests, setChangeRequests] = useState<any[]>([]);
  const [rejectReasonDialog, setRejectReasonDialog] = useState<{ open: boolean; requestId: string | null }>({ open: false, requestId: null });
  const [rejectReason, setRejectReason] = useState('');

  // 公開確認ダイアログ
  const [publishDialog, setPublishDialog] = useState<{ open: boolean; createdCount: number; employeeIds: string[] }>({
    open: false, createdCount: 0, employeeIds: [],
  });
  const [notifyOnPublish, setNotifyOnPublish] = useState(true);

  // フィルター
  // 既定値も UTC の今日だった。JST の 0〜9 時は前日を指す。
  // 出勤前に開くと、その日のシフトが1件も出ない。
  const [filterDate, setFilterDate] = useState(() =>
    getBusinessDate(new Date(), 'Asia/Tokyo', 0));
  const [filterEmployee, setFilterEmployee] = useState('');

  useEffect(() => {
    if (shop) {
      fetchEmployees();
      fetchShifts();
      fetchShiftRequests();
    }
  }, [shop]);

  // シフト変更希望をリアルタイム監視
  useEffect(() => {
    if (!shop) return;
    const q = query(
      collection(db, 'shiftChangeRequests'),
      where('shopId', '==', shop.id),
      where('status', '==', 'pending')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        shiftDate: d.data().shiftDate?.toDate ? d.data().shiftDate.toDate() : new Date(d.data().shiftDate),
        createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(),
      }));
      setChangeRequests(requests);
    });
    return () => unsubscribe();
  }, [shop]);

  const fetchEmployees = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'employees'),
        where('shopId', '==', shop.id),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => {
        const employeeData = doc.data();
        return {
          id: doc.id,
          ...employeeData,
          personalInfo: {
            ...employeeData.personalInfo,
            birthDate: employeeData.personalInfo?.birthDate?.toDate
              ? employeeData.personalInfo.birthDate.toDate()
              : new Date(),
          },
          employmentInfo: {
            ...employeeData.employmentInfo,
            hireDate: employeeData.employmentInfo?.hireDate?.toDate
              ? employeeData.employmentInfo.hireDate.toDate()
              : new Date(),
          },
          createdAt: employeeData.createdAt?.toDate
            ? employeeData.createdAt.toDate()
            : new Date(),
          updatedAt: employeeData.updatedAt?.toDate
            ? employeeData.updatedAt.toDate()
            : new Date(),
        };
      }) as Employee[];
      setEmployees(data);
    } catch (err) {
      console.error('従業員一覧の取得エラー:', err);
    }
  };

  const fetchShifts = async () => {
    if (!shop) return;

    try {
      setLoading(true);
      const q = query(
        collection(db, 'shifts'),
        where('shopId', '==', shop.id),
        orderBy('shiftDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => {
        const shiftData = doc.data();
        return {
          id: doc.id,
          ...shiftData,
          shiftDate: shiftData.shiftDate?.toDate
            ? shiftData.shiftDate.toDate()
            : new Date(),
          approvedAt: shiftData.approvedAt?.toDate
            ? shiftData.approvedAt.toDate()
            : undefined,
          createdAt: shiftData.createdAt?.toDate
            ? shiftData.createdAt.toDate()
            : new Date(),
          updatedAt: shiftData.updatedAt?.toDate
            ? shiftData.updatedAt.toDate()
            : new Date(),
        };
      }) as Shift[];
      setShifts(data);
    } catch (err) {
      console.error('シフト一覧の取得エラー:', err);
      setError('シフト一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchShiftRequests = async () => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'shiftRequests'),
        where('shopId', '==', shop.id),
        where('requestType', '==', 'shift_wanted'),
        where('status', '==', 'open')
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map((doc) => {
        const requestData = doc.data();
        return {
          id: doc.id,
          ...requestData,
          requestedAt: requestData.requestedAt?.toDate
            ? requestData.requestedAt.toDate()
            : new Date(),
          newShift: requestData.newShift
            ? {
                ...requestData.newShift,
                shiftDate: requestData.newShift.shiftDate?.toDate
                  ? requestData.newShift.shiftDate.toDate()
                  : new Date(),
              }
            : undefined,
          createdAt: requestData.createdAt?.toDate
            ? requestData.createdAt.toDate()
            : new Date(),
          updatedAt: requestData.updatedAt?.toDate
            ? requestData.updatedAt.toDate()
            : new Date(),
          responses: requestData.responses?.map((r: any) => ({
            ...r,
            respondedAt: r.respondedAt?.toDate ? r.respondedAt.toDate() : new Date(),
          })) || [],
        };
      }) as ShiftRequest[];
      setShiftRequests(data);
    } catch (err) {
      console.error('シフト募集の取得エラー:', err);
    }
  };

  const handleOpenDialog = (shift?: Shift) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (shift) {
      setEditingShift(shift);
      // タイムゾーンを考慮した日付フォーマット
      const year = shift.shiftDate.getFullYear();
      const month = String(shift.shiftDate.getMonth() + 1).padStart(2, '0');
      const day = String(shift.shiftDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      setFormData({
        employeeId: shift.employeeId,
        shiftDate: dateStr,
        startTime: shift.startTime,
        endTime: shift.endTime,
        breakMinutes: shift.breakMinutes.toString(),
        shiftType: shift.shiftType,
        note: shift.note || '',
        status: shift.status,
      });
    } else {
      setEditingShift(null);
      setFormData({
        employeeId: '',
        shiftDate: filterDate,
        startTime: '09:00',
        endTime: '18:00',
        breakMinutes: '60',
        shiftType: 'regular',
        note: '',
        status: 'pending',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingShift(null);
    setError('');
  };

  const handleSubmit = async () => {
    if (!shop) return;

    setError('');
    setSuccess('');

    // バリデーション
    if (!formData.employeeId || !formData.shiftDate) {
      setError('従業員とシフト日は必須です');
      return;
    }

    try {
      if (editingShift) {
        // 更新
        const updateShift = httpsCallable(functions, 'updateShift');
        await updateShift({
          shiftId: editingShift.id,
          shiftDate: formData.shiftDate,
          startTime: formData.startTime,
          endTime: formData.endTime,
          breakMinutes: clamp(formData.breakMinutes, 0, 24 * 60),
          shiftType: formData.shiftType,
          note: formData.note,
          status: formData.status,
        });

        setSuccess('シフトを更新しました');
      } else {
        // 新規作成
        const createShift = httpsCallable(functions, 'createShift');
        await createShift({
          shopId: shop.id,
          employeeId: formData.employeeId,
          shiftDate: formData.shiftDate,
          startTime: formData.startTime,
          endTime: formData.endTime,
          breakMinutes: clamp(formData.breakMinutes, 0, 24 * 60),
          shiftType: formData.shiftType,
          note: formData.note,
          status: formData.status,
        });

        setSuccess('シフトを作成しました');
      }

      handleCloseDialog();
      await fetchShifts();
    } catch (err: any) {
      console.error('シフトの保存エラー:', err);
      setError(err.message || 'シフトの保存に失敗しました');
    }
  };

  const handleApprove = async (shift: Shift) => {
    try {
      const approveShift = httpsCallable(functions, 'approveShift');
      await approveShift({ shiftId: shift.id });

      setSuccess('シフトを承認しました');
      await fetchShifts();
    } catch (err: any) {
      console.error('シフト承認エラー:', err);
      setError(err.message || 'シフトの承認に失敗しました');
    }
  };

  const handleApproveRequest = async (shift: Shift) => {
    try {
      const approveShiftRequestByManager = httpsCallable(functions, 'approveShiftRequestByManager');
      await approveShiftRequestByManager({
        shiftId: shift.id,
        approvedBy: shop?.id,
      });

      setSuccess('シフト希望を承認しました');
      await fetchShifts();
    } catch (err: any) {
      console.error('シフト希望承認エラー:', err);
      setError(err.message || 'シフト希望の承認に失敗しました');
    }
  };

  const handleRejectRequest = async (shift: Shift) => {
    const reason = window.prompt('却下理由を入力してください（任意）');
    if (reason === null) return; // キャンセル

    try {
      const rejectShiftRequestByManager = httpsCallable(functions, 'rejectShiftRequestByManager');
      await rejectShiftRequestByManager({
        shiftId: shift.id,
        rejectionReason: reason,
        rejectedBy: shop?.id,
      });

      setSuccess('シフト希望を却下しました');
      await fetchShifts();
    } catch (err: any) {
      console.error('シフト希望却下エラー:', err);
      setError(err.message || 'シフト希望の却下に失敗しました');
    }
  };

  const handleOpenDeleteDialog = (shift: Shift) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setDeleteTarget(shift);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDoc(doc(db, 'shifts', deleteTarget.id));
      setSuccess('シフトを削除しました');
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      handleCloseDialog(); // 編集ダイアログも閉じる
      await fetchShifts();
    } catch (err) {
      console.error('削除エラー:', err);
      setError('削除に失敗しました');
    }
  };

  // フィルタリングされたシフト
  const filteredShifts = shifts.filter((shift) => {
    if (filterEmployee && shift.employeeId !== filterEmployee) {
      return false;
    }
    if (filterDate) {
      // toISOString() は UTC の日付を返す。画面の表示は
      // toLocaleDateString('ja-JP') で JST なので、
      // 「見えている日付」と「絞り込みで当たる日付」が別物だった。
      // 19:00-05:00 のシフトは 21:00Z で保存されるため、
      // デモの 1622 件すべてが UTC 日付 ≠ JST 日付。
      // 表示された日付をそのまま入れると 0 件になる。
      // シフトは「その日の勤務」なので、店のタイムゾーンの暦日で比べる。
      const shiftDateStr = getBusinessDate(
        shift.shiftDate, shop?.timezone ?? 'Asia/Tokyo', 0);
      if (shiftDateStr !== filterDate) {
        return false;
      }
    }
    return true;
  });

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find((e) => e.id === employeeId);
    if (!employee) return '不明';
    return `${employee.personalInfo.lastName} ${employee.personalInfo.firstName}`;
  };

  // ガントチャートからシフト追加
  const handleAddShiftFromGantt = async (employeeId: string, startTime: string, endTime: string) => {
    // タイムゾーンを考慮した日付フォーマット
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    setFormData({
      employeeId,
      shiftDate: dateStr,
      startTime,
      endTime,
      breakMinutes: '60',
      shiftType: 'regular',
      note: '',
      status: 'approved',
    });
    setEditingShift(null);
    setOpenDialog(true);
  };

  // ガントチャートからシフト更新
  const handleUpdateShiftFromGantt = async (shiftId: string, startTime: string, endTime: string, employeeId?: string) => {
    try {
      console.log('handleUpdateShiftFromGantt called:', { shiftId, startTime, endTime, employeeId });
      if (!shop) {
        console.error('No shop found');
        return;
      }
      const shopId = shop.id;

      const shiftRef = doc(db, 'shifts', shiftId);
      const updateData: any = {
        startTime,
        endTime,
        updatedAt: Timestamp.now(),
      };

      if (employeeId) {
        updateData.employeeId = employeeId;
      }

      console.log('Updating Firestore with:', updateData);
      await updateDoc(shiftRef, updateData);
      console.log('Firestore updated successfully');

      // ローカル状態も更新
      setShifts((prevShifts) => {
        const updated = prevShifts.map((shift) =>
          shift.id === shiftId
            ? { ...shift, ...updateData, updatedAt: new Date() }
            : shift
        );
        console.log('Local state updated, shifts count:', updated.length);
        return updated;
      });
    } catch (error) {
      console.error('シフト更新エラー:', error);
      alert('シフトの更新に失敗しました');
    }
  };

  // ガントチャートからシフトクリック
  const handleShiftClickFromGantt = (shift: Shift) => {
    handleOpenDialog(shift);
  };

  // 週別ビューのセルクリック
  const handleWeeklyCellClick = (date: Date, hour: number) => {
    setSelectedDate(date);
    setTabValue(1); // 日別ビューに切り替え
  };

  // テンプレートからシフトを作成
  const handleCreateFromTemplate = async () => {
    if (!shop) {
      setError('店舗情報が取得できません');
      return;
    }

    console.log('=== テンプレートからシフト作成開始 ===');
    console.log('店舗ID:', shop.id);
    console.log('従業員数:', employees.length);
    console.log('選択日:', selectedDate);

    try {
      setError('');
      setSuccess('');

      // WeeklyViewと同じロジック: 選択日を起点に7日間
      const weekStart = new Date(selectedDate);
      weekStart.setHours(0, 0, 0, 0);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      console.log('週の開始:', weekStart);
      console.log('週の終了:', weekEnd);

      // テンプレートを持つスタッフのみフィルタ
      const employeesWithTemplate = employees.filter(emp => {
        console.log(`従業員 ${emp.personalInfo.lastName}:`, emp.weeklyShiftTemplate);
        return emp.weeklyShiftTemplate &&
          Object.values(emp.weeklyShiftTemplate as Record<string, { enabled?: boolean } | undefined>).some(day => day?.enabled);
      });

      console.log('テンプレート設定済みスタッフ数:', employeesWithTemplate.length);

      if (employeesWithTemplate.length === 0) {
        setError('週次シフトテンプレートが設定されているスタッフがいません');
        return;
      }

      let createdCount = 0;

      for (const employee of employeesWithTemplate) {
        if (!employee.weeklyShiftTemplate) continue;

        console.log(`\n${employee.personalInfo.lastName} のシフト作成中...`);

        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

        for (let i = 0; i < 7; i++) {
          const currentDate = new Date(weekStart);
          currentDate.setDate(currentDate.getDate() + i);
          const dayOfWeek = currentDate.getDay();
          const dayName = dayNames[dayOfWeek] as string;
          const template = (employee.weeklyShiftTemplate as Record<string, { enabled?: boolean; startTime?: string; endTime?: string; breakMinutes?: number } | undefined>)[dayName];

          console.log(`  ${dayName} (${currentDate.toLocaleDateString()}):`, template);

          if (template?.enabled) {
            // すでに同日のシフトが存在するかチェック
            const existingShift = shifts.find(shift =>
              shift.employeeId === employee.id &&
              shift.shiftDate.toDateString() === currentDate.toDateString()
            );

            if (existingShift) {
              console.log(`    スキップ: すでにシフトが存在`);
            } else {
              console.log(`    作成: ${template.startTime} - ${template.endTime}`);
              await addDoc(collection(db, 'shifts'), {
                shopId: shop.id,
                employeeId: employee.id,
                shiftDate: Timestamp.fromDate(currentDate),
                startTime: template.startTime,
                endTime: template.endTime,
                breakMinutes: 60,
                shiftType: 'regular',
                status: 'approved',
                note: 'テンプレートから自動作成',
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                createdBy: shop.id,
              });
              createdCount++;
            }
          }
        }
      }

      console.log('=== 作成完了 ===');
      console.log('作成件数:', createdCount);

      if (createdCount === 0) {
        setSuccess('新規作成するシフトはありませんでした（既に作成済み）');
      } else {
        // 公開確認ダイアログを表示
        const uniqueEmployeeIds = Array.from(new Set(employeesWithTemplate.map(e => e.id!).filter(Boolean)));
        setPublishDialog({ open: true, createdCount, employeeIds: uniqueEmployeeIds });
      }

      await fetchShifts(); // シフトを再取得
    } catch (error) {
      console.error('テンプレートからのシフト作成エラー:', error);
      setError(`シフトの作成に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    }
  };

  // シフト公開通知を送信
  const handlePublishNotify = async () => {
    if (!shop || !notifyOnPublish) {
      setPublishDialog({ open: false, createdCount: 0, employeeIds: [] });
      setSuccess(`${publishDialog.createdCount}件のシフトを作成しました`);
      return;
    }

    try {
      const notifyFn = httpsCallable(functions, 'notifyShiftPublished');
      const weekStart = new Date(selectedDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const period = `${weekStart.getMonth() + 1}/${weekStart.getDate()} - ${weekEnd.getMonth() + 1}/${weekEnd.getDate()}のシフト`;

      await notifyFn({
        shopId: shop.id,
        employeeIds: publishDialog.employeeIds,
        period,
      });

      setSuccess(`${publishDialog.createdCount}件のシフトを作成し、スタッフに通知しました`);
    } catch (err) {
      console.error('通知送信エラー:', err);
      setSuccess(`${publishDialog.createdCount}件のシフトを作成しました（通知の送信に失敗しました）`);
    }

    setPublishDialog({ open: false, createdCount: 0, employeeIds: [] });
  };

  // シフト変更希望を承認
  const handleApproveChangeRequest = async (requestId: string) => {
    try {
      await updateDoc(doc(db, 'shiftChangeRequests', requestId), {
        status: 'approved',
        reviewedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setSuccess('変更希望を承認しました');
    } catch (err) {
      setError('承認に失敗しました');
    }
  };

  // シフト変更希望を却下
  const handleRejectChangeRequest = async () => {
    if (!rejectReasonDialog.requestId) return;
    try {
      await updateDoc(doc(db, 'shiftChangeRequests', rejectReasonDialog.requestId), {
        status: 'rejected',
        reviewNote: rejectReason,
        reviewedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setRejectReasonDialog({ open: false, requestId: null });
      setRejectReason('');
      setSuccess('変更希望を却下しました');
    } catch (err) {
      setError('却下に失敗しました');
    }
  };

  // シフト募集ダイアログを開く
  const handleOpenRequestDialog = () => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    setRequestFormData({
      shiftDate: `${year}-${month}-${day}`,
      startTime: '18:00',
      endTime: '23:00',
      breakMinutes: '0',
      requiredStaffCount: '1',
      position: '',
      message: '',
    });
    setOpenRequestDialog(true);
  };

  // シフト募集を作成
  const handleCreateRequest = async () => {
    if (!shop) return;

    setError('');
    setSuccess('');

    if (!requestFormData.shiftDate || !requestFormData.requiredStaffCount) {
      setError('日付と必要人数は必須です');
      return;
    }
    // "-3" は truthy なので上の検証を通り抜ける。
    // 実際に requiredStaffCount=-3 が保存され、一覧には「-3名募集」と出た。
    // さらに日別ビューは Array.from({ length: count }) で枠を展開するので、
    // 負だと長さ0になり、募集そのものがガントから消える。
    // 記録は残っているのに誰の目にも触れない募集ができていた。
    const needed = parseInt(String(requestFormData.requiredStaffCount), 10);
    if (!Number.isFinite(needed) || needed < 1) {
      setError('必要人数は1以上にしてください');
      return;
    }

    try {
      const newShiftData: any = {
        shiftDate: Timestamp.fromDate(new Date(requestFormData.shiftDate)),
        startTime: requestFormData.startTime,
        endTime: requestFormData.endTime,
        breakMinutes: parseInt(requestFormData.breakMinutes),
        requiredStaffCount: parseInt(requestFormData.requiredStaffCount),
      };

      // positionが空でない場合のみ追加
      if (requestFormData.position) {
        newShiftData.position = requestFormData.position;
      }

      const requestData: any = {
        shopId: shop.id,
        requestType: 'shift_wanted',
        requestedBy: shop.id,
        requestedAt: Timestamp.now(),
        newShift: newShiftData,
        isOpenToAll: true,
        status: 'open',
        responses: [],
        title: `${requestFormData.requiredStaffCount}名募集（${requestFormData.startTime}-${requestFormData.endTime}）`,
        createdAt: Timestamp.now(),
      };

      // messageが空でない場合のみ追加
      if (requestFormData.message) {
        requestData.message = requestFormData.message;
      }

      await addDoc(collection(db, 'shiftRequests'), requestData);

      setSuccess('シフト募集を作成しました');
      setOpenRequestDialog(false);
      await fetchShiftRequests();
    } catch (err: any) {
      console.error('シフト募集の作成エラー:', err);
      setError(err.message || 'シフト募集の作成に失敗しました');
    }
  };

  // シフト募集を更新（ドラッグ&リサイズ用）
  const handleUpdateRequest = async (requestId: string, startTime: string, endTime: string) => {
    try {
      const requestRef = doc(db, 'shiftRequests', requestId);
      const request = shiftRequests.find(r => r.id === requestId);
      if (!request || !request.newShift) return;

      await updateDoc(requestRef, {
        'newShift.startTime': startTime,
        'newShift.endTime': endTime,
        updatedAt: Timestamp.now(),
      });

      // ローカル状態を更新
      setShiftRequests(prevRequests =>
        prevRequests.map(req =>
          req.id === requestId && req.newShift
            ? {
                ...req,
                newShift: {
                  ...req.newShift,
                  startTime,
                  endTime,
                },
                updatedAt: new Date(),
              }
            : req
        )
      );
    } catch (error) {
      console.error('シフト募集の更新エラー:', error);
      alert('シフト募集の更新に失敗しました');
    }
  };

  // シフト募集を削除（必要人数を1減らす）
  const handleDeleteRequest = async (requestId: string) => {
    try {
      // 現在の募集情報を取得
      const request = shiftRequests.find(r => r.id === requestId);
      if (!request || !request.newShift) return;

      const currentCount = request.newShift.requiredStaffCount || 1;

      if (currentCount <= 1) {
        // 必要人数が1の場合は募集全体を削除
        if (!window.confirm('この募集を完全に削除してもよろしいですか？')) return;
        await deleteDoc(doc(db, 'shiftRequests', requestId));
        setSuccess('シフト募集を削除しました');
      } else {
        // 必要人数を1減らす
        if (!window.confirm(`募集人数を${currentCount}名から${currentCount - 1}名に減らしますか？`)) return;
        const requestRef = doc(db, 'shiftRequests', requestId);
        await updateDoc(requestRef, {
          'newShift.requiredStaffCount': currentCount - 1,
          'title': `${currentCount - 1}名募集（${request.newShift.startTime}-${request.newShift.endTime}）`,
          'updatedAt': Timestamp.now(),
        });
        setSuccess(`募集人数を${currentCount - 1}名に更新しました`);
      }

      await fetchShiftRequests();
    } catch (err) {
      console.error('シフト募集の削除エラー:', err);
      setError('シフト募集の更新に失敗しました');
    }
  };

  // シフト募集の必要人数を1増やす
  const handleIncreaseRequest = async (requestId: string) => {
    try {
      const request = shiftRequests.find(r => r.id === requestId);
      if (!request || !request.newShift) return;

      const currentCount = request.newShift.requiredStaffCount || 1;

      if (!window.confirm(`募集人数を${currentCount}名から${currentCount + 1}名に増やしますか？`)) return;

      const requestRef = doc(db, 'shiftRequests', requestId);
      await updateDoc(requestRef, {
        'newShift.requiredStaffCount': currentCount + 1,
        'title': `${currentCount + 1}名募集（${request.newShift.startTime}-${request.newShift.endTime}）`,
        'updatedAt': Timestamp.now(),
      });

      setSuccess(`募集人数を${currentCount + 1}名に更新しました`);
      await fetchShiftRequests();
    } catch (err) {
      console.error('シフト募集の増員エラー:', err);
      setError('シフト募集の更新に失敗しました');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">シフト管理</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<CalendarIcon />} onClick={handleCreateFromTemplate}>
            テンプレートから作成
          </Button>
          <Button variant="outlined" color="warning" startIcon={<AddIcon />} onClick={handleOpenRequestDialog}>
            シフト募集
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            シフト追加
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* シフト変更希望パネル */}
      {changeRequests.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            <Badge badgeContent={changeRequests.length} color="warning" sx={{ mr: 2 }}>
              <NotificationsIcon color="warning" />
            </Badge>
            スタッフからの変更希望（{changeRequests.length}件）
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>スタッフ</TableCell>
                <TableCell>日付</TableCell>
                <TableCell>時間</TableCell>
                <TableCell>種別</TableCell>
                <TableCell>理由</TableCell>
                <TableCell align="center">対応</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changeRequests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell>{req.employeeName}</TableCell>
                  <TableCell>{req.shiftDate.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', weekday: 'short' })}</TableCell>
                  <TableCell>{req.startTime} - {req.endTime}</TableCell>
                  <TableCell>
                    <Chip
                      label={req.changeType === 'dayOff' ? '休み希望' : '交代希望'}
                      size="small"
                      color={req.changeType === 'dayOff' ? 'error' : 'info'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {req.reason}
                  </TableCell>
                  <TableCell align="center">
                    <Button size="small" color="success" onClick={() => handleApproveChangeRequest(req.id)} sx={{ mr: 1 }}>
                      承認
                    </Button>
                    <Button size="small" color="error" onClick={() => { setRejectReasonDialog({ open: true, requestId: req.id }); setRejectReason(''); }}>
                      却下
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab icon={<ViewWeekIcon />} label="週別ビュー" iconPosition="start" />
        <Tab icon={<ViewDayIcon />} label="日別ビュー" iconPosition="start" />
        <Tab icon={<CalendarIcon />} label="リスト表示" iconPosition="start" />
      </Tabs>

      {/* タブ0: 週別ビュー */}
      <TabPanel value={tabValue} index={0}>
        <WeeklyView
          shifts={shifts}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onCellClick={handleWeeklyCellClick}
        />
      </TabPanel>

      {/* タブ1: 日別ビュー */}
      <TabPanel value={tabValue} index={1}>
        <DailyView
          boundaryHour={shop?.businessDayBoundaryTime}
          shifts={shifts}
          shiftRequests={shiftRequests}
          employees={employees}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onShiftClick={handleShiftClickFromGantt}
          onAddShift={handleAddShiftFromGantt}
          onUpdateShift={handleUpdateShiftFromGantt}
          onUpdateRequest={handleUpdateRequest}
          onDeleteRequest={handleDeleteRequest}
          onIncreaseRequest={handleIncreaseRequest}
        />
      </TabPanel>

      {/* タブ2: リスト表示 */}
      <TabPanel value={tabValue} index={2}>
        {/* フィルター */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="シフト日"
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="従業員"
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
              >
                <MenuItem value="">全員</MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.personalInfo.lastName} {employee.personalInfo.firstName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setFilterDate('');
                  setFilterEmployee('');
                }}
              >
                フィルタークリア
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>シフト日</TableCell>
                <TableCell>従業員名</TableCell>
                <TableCell>勤務時間</TableCell>
                <TableCell>休憩</TableCell>
                <TableCell>タイプ</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>メモ</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShifts.map((shift) => (
                <TableRow key={shift.id}>
                  <TableCell>
                    {shift.shiftDate.toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>{getEmployeeName(shift.employeeId)}</TableCell>
                  <TableCell>
                    {shift.startTime} - {shift.endTime}
                  </TableCell>
                  <TableCell>{shift.breakMinutes}分</TableCell>
                  <TableCell>{shiftTypeLabels[shift.shiftType]}</TableCell>
                  <TableCell>
                    <Chip
                      label={shiftStatusLabels[shift.status]}
                      color={shiftStatusColors[shift.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{shift.note || '-'}</TableCell>
                  <TableCell>
                    {shift.status === 'requested' && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleApproveRequest(shift)}
                          color="success"
                          title="希望承認"
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRejectRequest(shift)}
                          color="error"
                          title="希望却下"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                    {shift.status === 'pending' && (
                      <IconButton
                        size="small"
                        onClick={() => handleApprove(shift)}
                        color="success"
                        title="承認"
                      >
                        <CheckIcon />
                      </IconButton>
                    )}
                    {(shift.status === 'pending' || shift.status === 'approved') && (
                      <IconButton size="small" onClick={() => handleOpenDialog(shift)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(shift)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredShifts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    シフトが登録されていません
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>


      {/* シフト追加・編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingShift ? 'シフト編集' : 'シフト追加'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="従業員"
                value={formData.employeeId}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                required
                disabled={!!editingShift}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.personalInfo.lastName} {employee.personalInfo.firstName} (
                    {employee.employmentInfo.position})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="シフト日"
                type="date"
                value={formData.shiftDate}
                onChange={(e) => setFormData({ ...formData, shiftDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="開始時刻"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="終了時刻"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="休憩時間"
                type="number"
                value={formData.breakMinutes}
                onChange={(e) => setFormData({ ...formData, breakMinutes: e.target.value })}
                InputProps={{ endAdornment: '分' }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="シフトタイプ"
                value={formData.shiftType}
                onChange={(e) =>
                  setFormData({ ...formData, shiftType: e.target.value as typeof formData.shiftType })
                }
              >
                {Object.entries(shiftTypeLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="ステータス"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as typeof formData.status })
                }
              >
                {Object.entries(shiftStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="メモ"
                multiline
                rows={3}
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Box>
              {editingShift && (
                <Button
                  onClick={() => handleOpenDeleteDialog(editingShift)}
                  color="error"
                  startIcon={<DeleteIcon />}
                >
                  削除
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleCloseDialog}>キャンセル</Button>
              <Button onClick={handleSubmit} variant="contained">
                {editingShift ? '更新' : '作成'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>シフト削除</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Typography>
              {getEmployeeName(deleteTarget.employeeId)} の
              {deleteTarget.shiftDate.toLocaleDateString('ja-JP')} のシフトを削除してもよろしいですか？
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* シフト公開確認ダイアログ */}
      <Dialog open={publishDialog.open} onClose={() => setPublishDialog({ open: false, createdCount: 0, employeeIds: [] })}>
        <DialogTitle>シフトを公開して通知しますか？</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            作成されたシフト: <strong>{publishDialog.createdCount}件</strong>
          </Typography>
          <Typography sx={{ mb: 2 }}>
            対象スタッフ: <strong>{publishDialog.employeeIds.length}名</strong>
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={notifyOnPublish} onChange={(e) => setNotifyOnPublish(e.target.checked)} />}
            label="スタッフにLINE通知を送信する"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPublishDialog({ open: false, createdCount: 0, employeeIds: [] });
            setSuccess(`${publishDialog.createdCount}件のシフトを作成しました`);
          }}>
            通知しない
          </Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={handlePublishNotify}>
            公開して通知
          </Button>
        </DialogActions>
      </Dialog>

      {/* 却下理由ダイアログ */}
      <Dialog open={rejectReasonDialog.open} onClose={() => setRejectReasonDialog({ open: false, requestId: null })}>
        <DialogTitle>変更希望を却下</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="却下理由"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="却下の理由を入力してください"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectReasonDialog({ open: false, requestId: null })}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleRejectChangeRequest}>却下する</Button>
        </DialogActions>
      </Dialog>

      {/* シフト募集ダイアログ */}
      <Dialog open={openRequestDialog} onClose={() => setOpenRequestDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>シフト募集</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="募集日"
                type="date"
                value={requestFormData.shiftDate}
                onChange={(e) => setRequestFormData({ ...requestFormData, shiftDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="開始時刻"
                type="time"
                value={requestFormData.startTime}
                onChange={(e) => setRequestFormData({ ...requestFormData, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="終了時刻"
                type="time"
                value={requestFormData.endTime}
                onChange={(e) => setRequestFormData({ ...requestFormData, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="必要人数"
                type="number"
                value={requestFormData.requiredStaffCount}
                onChange={(e) => setRequestFormData({ ...requestFormData, requiredStaffCount: e.target.value })}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="休憩時間"
                type="number"
                value={requestFormData.breakMinutes}
                onChange={(e) => setRequestFormData({ ...requestFormData, breakMinutes: e.target.value })}
                InputProps={{ endAdornment: '分' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="職種・ポジション"
                value={requestFormData.position}
                onChange={(e) => setRequestFormData({ ...requestFormData, position: e.target.value })}
                placeholder="例: ホール、キッチン"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="メッセージ"
                multiline
                rows={3}
                value={requestFormData.message}
                onChange={(e) => setRequestFormData({ ...requestFormData, message: e.target.value })}
                placeholder="募集の詳細や条件などを記入"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRequestDialog(false)}>キャンセル</Button>
          <Button onClick={handleCreateRequest} variant="contained" color="warning">
            募集を作成
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
